import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import type { GgPerguntaDto } from "@/services/genteGestao";
import type { PesquisaPublicaMeta } from "@/services/pesquisaNps";

const ESCALA: Record<number, string> = {
  1: "Discordo totalmente",
  2: "Discordo",
  3: "Neutro",
  4: "Concordo",
  5: "Concordo totalmente",
};

export type RespostaMap = Record<string, { valorNumero: number | null; valorTexto: string | null }>;

interface Draft {
  step: number;
  answers: RespostaMap;
}

function readDraft(key: string): Draft | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Draft;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeDraft(key: string, draft: Draft): void {
  try {
    localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    /* quota / privado */
  }
}

function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function FormularioNps({
  pesquisa,
  perguntas,
  colaboradorNome,
  storageKey,
  onSubmit,
  submitting,
}: {
  pesquisa: PesquisaPublicaMeta;
  perguntas: GgPerguntaDto[];
  colaboradorNome?: string;
  storageKey: string;
  onSubmit: (answers: RespostaMap) => Promise<void>;
  submitting: boolean;
}) {
  const [fase, setFase] = useState<"abertura" | "perguntas" | "revisao" | "ok">("abertura");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<RespostaMap>({});
  const [erro, setErro] = useState<string | null>(null);
  const atual = perguntas[step];
  const total = perguntas.length;

  useEffect(() => {
    const draft = readDraft(storageKey);
    if (draft?.answers) setAnswers(draft.answers);
    if (typeof draft?.step === "number") setStep(Math.min(draft.step, Math.max(0, perguntas.length - 1)));
  }, [storageKey, perguntas.length]);

  useEffect(() => {
    if (fase === "perguntas") writeDraft(storageKey, { step, answers });
  }, [fase, step, answers, storageKey]);

  useEffect(() => {
    if (fase !== "revisao") return;
    const scroller = document.querySelector("[data-nps-scroll]");
    if (scroller instanceof HTMLElement) scroller.scrollTo({ top: 0 });
    else window.scrollTo({ top: 0 });
  }, [fase]);

  const setNumero = useCallback((perguntaId: string, n: number) => {
    setAnswers((prev) => ({ ...prev, [perguntaId]: { valorNumero: n, valorTexto: null } }));
    setErro(null);
  }, []);

  const setTexto = useCallback((perguntaId: string, t: string) => {
    setAnswers((prev) => ({ ...prev, [perguntaId]: { valorNumero: null, valorTexto: t } }));
    setErro(null);
  }, []);

  function valorAtual(p: GgPerguntaDto) {
    return answers[p.id];
  }

  function valida(p: GgPerguntaDto): boolean {
    if (!p.obrigatoria) return true;
    const v = valorAtual(p);
    if (p.tipo === "TEXTO") return Boolean(v?.valorTexto?.trim());
    return v?.valorNumero != null;
  }

  function avancar() {
    if (!atual) return;
    if (!valida(atual)) {
      setErro("Esta pergunta é obrigatória.");
      return;
    }
    if (step + 1 >= total) setFase("revisao");
    else setStep((s) => s + 1);
  }

  useEffect(() => {
    if (fase !== "perguntas" || !atual || atual.tipo !== "NPS_0_10") return;
    let pending1 = false;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        avancar();
        return;
      }
      if (e.key >= "0" && e.key <= "9") {
        const n = Number(e.key);
        if (pending1 && n === 0) {
          setNumero(atual.id, 10);
          pending1 = false;
          return;
        }
        pending1 = n === 1;
        setNumero(atual.id, n);
        window.setTimeout(() => {
          pending1 = false;
        }, 400);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase, atual?.id, atual?.tipo]);

  useEffect(() => {
    if (fase !== "perguntas" || !atual || atual.tipo !== "ESCALA_1_5") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        avancar();
        return;
      }
      if (e.key >= "1" && e.key <= "5") setNumero(atual.id, Number(e.key));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase, atual?.id, atual?.tipo]);

  const resumo = useMemo(() => {
    return perguntas.map((p) => {
      const v = answers[p.id];
      let label = "—";
      if (p.tipo === "TEXTO") label = v?.valorTexto?.trim() ? v.valorTexto : "—";
      else if (v?.valorNumero != null) {
        label =
          p.tipo === "ESCALA_1_5"
            ? `${v.valorNumero} – ${ESCALA[v.valorNumero]}`
            : String(v.valorNumero);
      }
      return { id: p.id, texto: p.texto, label, ok: valida(p) };
    });
  }, [perguntas, answers]);

  if (fase === "abertura") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
        <p className="text-sm text-slate-500">{colaboradorNome ? `Olá, ${colaboradorNome}!` : "Olá!"}</p>
        <h1 className="mt-1 text-2xl font-bold">{pesquisa.titulo}</h1>
        {pesquisa.descricao && <p className="mt-2 text-sm text-slate-600">{pesquisa.descricao}</p>}
        <p className="mt-3 text-sm text-slate-500">Tempo estimado: cerca de {pesquisa.tempoEstimadoMinutos} min.</p>
        <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm">
          <p className="font-semibold">{pesquisa.privacidade.titulo}</p>
          <p className="mt-1 text-slate-600">{pesquisa.privacidade.corpo}</p>
          <p className="mt-2 text-xs text-slate-500">{pesquisa.privacidade.lgpd}</p>
        </div>
        <Button className="mt-5 w-full" size="lg" onClick={() => setFase("perguntas")}>
          Começar
        </Button>
      </div>
    );
  }

  if (fase === "ok") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-card">
        <h1 className="text-2xl font-bold">Obrigado!</h1>
        <p className="mt-2 text-sm text-slate-600">Sua resposta foi registrada. A {pesquisa.nomeEmpresa} agradece.</p>
      </div>
    );
  }

  if (fase === "revisao") {
    return (
      <div className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-card">
        <div className="p-5">
          <h1 className="text-lg font-bold">Revisar respostas</h1>
          <ul className="mt-3 flex flex-col gap-2">
            {resumo.map((r, i) => (
              <li key={r.id} className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="text-xs text-slate-500">
                  {i + 1}. {r.texto}
                </p>
                <p className={cn("mt-1 break-words", r.ok ? "text-slate-800" : "text-red-600")}>{r.label}</p>
              </li>
            ))}
          </ul>
          {erro && <p className="mt-2 text-sm text-red-600">{erro}</p>}
        </div>
        <div className="sticky bottom-0 flex gap-2 border-t border-slate-100 bg-white p-4">
          <Button variant="secondary" className="flex-1" onClick={() => setFase("perguntas")}>
            Voltar
          </Button>
          <Button
            className="flex-1"
            loading={submitting}
            onClick={() => {
              const faltando = perguntas.filter((p) => !valida(p));
              if (faltando.length) {
                setErro("Há perguntas obrigatórias sem resposta.");
                return;
              }
              void onSubmit(answers)
                .then(() => {
                  clearDraft(storageKey);
                  setFase("ok");
                })
                .catch((e: unknown) => {
                  const ax = e as { response?: { data?: { error?: string; estado?: string } } };
                  if (ax.response?.data?.estado === "ja_respondeu") {
                    clearDraft(storageKey);
                    setFase("ok");
                    return;
                  }
                  setErro(ax.response?.data?.error || "Não foi possível enviar.");
                });
            }}
          >
            Enviar respostas
          </Button>
        </div>
      </div>
    );
  }

  if (!atual) return null;
  const v = valorAtual(atual);

  return (
    <div className="flex min-h-[70dvh] flex-col rounded-2xl border border-slate-200 bg-white shadow-card">
      <div className="px-4 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Pergunta {step + 1} de {total}
        </p>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full bg-brand-600" style={{ width: `${((step + 1) / total) * 100}%` }} />
        </div>
        <h2 className="mt-4 text-lg font-semibold leading-snug">{atual.texto}</h2>
        {!atual.obrigatoria && <p className="mt-1 text-xs text-slate-400">Opcional</p>}
      </div>

      <div className="flex-1 px-4 py-4">
        {atual.tipo === "NPS_0_10" && (
          <div>
            <div className="grid grid-cols-6 gap-2 sm:grid-cols-11">
              {Array.from({ length: 11 }, (_, n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNumero(atual.id, n)}
                  className={cn(
                    "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border text-sm font-semibold",
                    v?.valorNumero === n
                      ? "border-brand-600 bg-brand-600 text-white"
                      : "border-slate-200 bg-slate-50 text-slate-700",
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-slate-500">
              <span>Nada provável</span>
              <span>Muito provável</span>
            </div>
          </div>
        )}
        {atual.tipo === "ESCALA_1_5" && (
          <div className="flex flex-col gap-2 sm:flex-row">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setNumero(atual.id, n)}
                className={cn(
                  "min-h-[44px] flex-1 rounded-lg border px-3 py-2 text-left text-sm font-medium sm:text-center",
                  v?.valorNumero === n
                    ? "border-brand-600 bg-brand-600 text-white"
                    : "border-slate-200 bg-slate-50 text-slate-700",
                )}
              >
                {n} – {ESCALA[n]}
              </button>
            ))}
          </div>
        )}
        {atual.tipo === "TEXTO" && (
          <div>
            <textarea
              className="min-h-[140px] w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
              maxLength={1000}
              value={v?.valorTexto ?? ""}
              onChange={(e) => setTexto(atual.id, e.target.value.slice(0, 1000))}
            />
            <p className="mt-1 text-right text-xs text-slate-400">{(v?.valorTexto ?? "").length}/1000</p>
          </div>
        )}
        {erro && <p className="mt-3 text-sm text-red-600">{erro}</p>}
      </div>

      <div className="sticky bottom-0 flex gap-2 border-t border-slate-100 bg-white p-4">
        <Button
          variant="secondary"
          className="flex-1"
          disabled={step === 0}
          onClick={() => {
            setErro(null);
            setStep((s) => Math.max(0, s - 1));
          }}
        >
          Voltar
        </Button>
        <Button className="flex-1" onClick={avancar}>
          {step + 1 >= total ? "Revisar" : "Próxima"}
        </Button>
      </div>
    </div>
  );
}
